import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { useTheme } from "../../contexts/ThemeContext";
import {
  CLASS_COLORS,
  CLASS_LABELS,
  getDistributionTimezone,
  type ClassKey,
  type OccupancyTrendPoint,
} from "../../services/peopleDistributionTypes";
import { getLocalDateString } from "../../services/peopleDistributionTypes";
import {
  buildTimeAxisConfig,
  buildTrendChartDataZoom,
  formatAxisTooltipTime,
  formatPeakMarkLabel,
  getRecentWindowDataZoomRange,
  getZonedMinuteKey,
  resolveChartPeakTimestamp,
  toTimeSeriesData,
} from "./chartTimeAxis";

type OccupancyTrendChartProps = {
  data: OccupancyTrendPoint[];
  activeClasses: ClassKey[];
  peakOccupancy?: number;
  peakBucketTime?: string;
  peakOccupancyByType?: Record<string, number>;
  selectedDate?: string;
};

type TooltipParam = {
  axisValue?: number;
  seriesName?: string;
  color?: string;
  value?: number | [number, number];
};

function seriesValue(value: number | [number, number] | undefined): number {
  if (value == null) return 0;
  return Array.isArray(value) ? (value[1] ?? 0) : value;
}

function hasClassBreakdown(breakdown?: Record<string, number>): boolean {
  if (!breakdown) return false;
  return Object.values(breakdown).some((value) => value > 0);
}

function buildOccupancyTooltipFormatter(
  data: OccupancyTrendPoint[],
  activeClasses: ClassKey[],
  timezone: string,
  peakBucketTime?: string,
  peakOccupancy?: number,
  peakOccupancyByType?: Record<string, number>
) {
  return (raw: unknown) => {
    const params = (Array.isArray(raw) ? raw : [raw]) as TooltipParam[];
    if (!params.length || params[0].axisValue == null) return "";

    const axisValue = params[0].axisValue;
    const hoverKey = getZonedMinuteKey(new Date(axisValue).toISOString(), timezone);
    const peakKey = peakBucketTime
      ? getZonedMinuteKey(peakBucketTime, timezone)
      : null;
    const atPeakMinute = peakKey != null && hoverKey === peakKey;

    const trendPoint = data.find(
      (point) => getZonedMinuteKey(point.bucketTime, timezone) === hoverKey
    );

    const breakdown =
      atPeakMinute && hasClassBreakdown(peakOccupancyByType)
        ? peakOccupancyByType
        : hasClassBreakdown(trendPoint?.occupancyByType)
          ? trendPoint?.occupancyByType
          : peakOccupancyByType;

    const lines = [formatAxisTooltipTime(axisValue, timezone)];

    for (const param of params) {
      const name = param.seriesName ?? "";
      let value = seriesValue(param.value);

      if (name === "Total" && atPeakMinute && peakOccupancy != null) {
        value = peakOccupancy;
      } else if (name !== "Total") {
        const classKey = activeClasses.find((key) => CLASS_LABELS[key] === name);
        if (classKey && breakdown?.[classKey] != null) {
          value = breakdown[classKey];
        }
      }

      const marker = param.color
        ? `<span style="display:inline-block;margin-right:4px;border-radius:50%;width:8px;height:8px;background:${param.color};"></span>`
        : "";
      lines.push(`${marker}${name}: <strong>${Math.round(value)}</strong>`);
    }

    return lines.join("<br/>");
  };
}

export const OccupancyTrendChart: React.FC<OccupancyTrendChartProps> = ({
  data,
  activeClasses,
  peakOccupancy,
  peakBucketTime,
  peakOccupancyByType,
  selectedDate = getLocalDateString(),
}) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const timezone = getDistributionTimezone();

  const axisColor = isDark ? "#94A3B8" : "#64748B";
  const gridColor = isDark ? "#334155" : "#E2E8F0";
  const tooltipBg = isDark ? "#1e293b" : "#fff";
  const tooltipText = isDark ? "#e2e8f0" : "#0f172a";

  const option = useMemo(() => {
    const bucketTimes = data.map((point) => point.bucketTime);
    const totalValues = data.map((point) => point.totalOccupancy);
    const totalSeries = toTimeSeriesData(bucketTimes, totalValues);

    const peakTimestamp = resolveChartPeakTimestamp(
      data,
      timezone,
      peakBucketTime,
      peakOccupancy
    );
    const peakDataIndex =
      peakBucketTime != null
        ? data.findIndex(
            (point) =>
              getZonedMinuteKey(point.bucketTime, timezone) ===
              getZonedMinuteKey(peakBucketTime, timezone)
          )
        : data.findIndex((point) => point.totalOccupancy === peakOccupancy);
    const peakTimeLabel =
      peakTimestamp != null ? formatPeakMarkLabel(peakTimestamp, timezone) : "";
    const peakValueLabel =
      peakOccupancy != null && peakOccupancy > 0 ? String(Math.round(peakOccupancy)) : "";
    const peakMarkLabel =
      peakValueLabel && peakTimeLabel
        ? `Peak ${peakValueLabel}\n${peakTimeLabel}`
        : peakValueLabel
          ? `Peak ${peakValueLabel}`
          : peakTimeLabel
            ? `Peak\n${peakTimeLabel}`
            : "Peak";

    const classSeries = activeClasses.map((key) => ({
      name: CLASS_LABELS[key],
      type: "line" as const,
      smooth: true,
      symbol: "none",
      lineStyle: { width: 1.5 },
      itemStyle: { color: CLASS_COLORS[key] },
      data: toTimeSeriesData(
        bucketTimes,
        data.map((point) => point.occupancyByType?.[key] ?? 0)
      ),
    }));

    const { start: zoomStart, end: zoomEnd } = getRecentWindowDataZoomRange(
      bucketTimes,
      selectedDate,
      timezone
    );

    return {
      tooltip: {
        trigger: "axis",
        backgroundColor: tooltipBg,
        borderColor: gridColor,
        textStyle: { color: tooltipText, fontSize: 12 },
        axisPointer: { type: "line", snap: true },
        formatter: buildOccupancyTooltipFormatter(
          data,
          activeClasses,
          timezone,
          peakBucketTime,
          peakOccupancy,
          peakOccupancyByType
        ),
      },
      legend: {
        bottom: 28,
        left: 0,
        icon: "circle",
        itemWidth: 8,
        itemHeight: 8,
        textStyle: { color: axisColor, fontSize: 11 },
        data: ["Total", ...activeClasses.map((key) => CLASS_LABELS[key])],
      },
      grid: { left: 48, right: 16, top: 32, bottom: 64 },
      dataZoom: buildTrendChartDataZoom(
        isDark,
        axisColor,
        CLASS_COLORS.total,
        zoomStart,
        zoomEnd
      ),
      xAxis: buildTimeAxisConfig(selectedDate, timezone, axisColor, gridColor),
      yAxis: {
        type: "value",
        axisLine: { show: false },
        splitLine: { lineStyle: { color: gridColor, type: "dashed" } },
        axisLabel: { color: axisColor, fontSize: 11 },
      },
      series: [
        {
          name: "Total",
          type: "line",
          smooth: false,
          symbol: "circle",
          showSymbol: true,
          symbolSize: (_value: number, params: { dataIndex: number }) =>
            params.dataIndex === peakDataIndex ? 10 : 4,
          emphasis: { focus: "series", scale: true },
          lineStyle: { width: 2.5, color: CLASS_COLORS.total },
          itemStyle: {
            color: (params: { dataIndex: number }) =>
              params.dataIndex === peakDataIndex ? CLASS_COLORS.peak : CLASS_COLORS.total,
          },
          areaStyle: { color: CLASS_COLORS.total, opacity: 0.12 },
          data: totalSeries,
          markLine:
            peakTimestamp != null
              ? {
                  silent: true,
                  symbol: "none",
                  lineStyle: { color: CLASS_COLORS.peak, type: "dashed", width: 1.5 },
                  label: {
                    formatter: peakMarkLabel,
                    color: CLASS_COLORS.peak,
                    fontSize: 10,
                    fontWeight: 600,
                    position: "end",
                    lineHeight: 14,
                  },
                  data: [{ xAxis: peakTimestamp }],
                }
              : undefined,
          markPoint:
            peakTimestamp != null && peakOccupancy != null
              ? {
                  silent: true,
                  symbol: "circle",
                  symbolSize: 10,
                  itemStyle: { color: CLASS_COLORS.peak },
                  data: [{ coord: [peakTimestamp, peakOccupancy] }],
                }
              : undefined,
        },
        ...classSeries,
      ],
    };
  }, [
    data,
    activeClasses,
    peakOccupancy,
    peakBucketTime,
    peakOccupancyByType,
    selectedDate,
    timezone,
    isDark,
    axisColor,
    gridColor,
    tooltipBg,
    tooltipText,
  ]);

  if (!data.length) {
    return (
      <div className="flex min-h-[280px] items-center justify-center rounded-lg border border-dashed border-slate-200 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
        No occupancy trend data available.
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
          Concurrent occupancy
        </h3>
        <span className="rounded-full border border-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:border-slate-600 dark:text-slate-400">
          1-min Bucket
        </span>
      </div>
      <ReactECharts option={option} style={{ height: 300, width: "100%" }} notMerge lazyUpdate />
    </div>
  );
};

export default OccupancyTrendChart;
